import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ia } from 'lib/helpers/utility';

import { showAlert } from '../../Alert/Alert';

const normalize = (s) => (s ? (ia(s) ? s : [s]) : []);

const stepsEqual = (a, b) => {
  if (a === b) return true;
  if (!ia(a) || !ia(b) || a.length !== b.length) return false;
  return a.every((item, i) => item === b[i] || item?.name === b[i]?.name);
};

/** @typedef {import('../lib/propTypes').BreadcrumbStep} BreadcrumbStep */
/** @typedef {import('../lib/propTypes').BreadcrumbContext} BreadcrumbCtx */

/**
 * Hook for managing breadcrumb navigation state.
 *
 * @param {BreadcrumbStep|BreadcrumbStep[]} s - Initial step(s)
 * @param {number} [initialStep=0] - Initial active step index
 * @returns {BreadcrumbCtx}
 */
function useBreadcrumb(s, initialStep = 0) {
  const navigate = useNavigate();
  const [steps, setSteps] = useState(() => normalize(s));
  const [step, _setStep] = useState(initialStep);
  const stepRef = useRef(initialStep);
  const directionRef = useRef(null);
  const prevRef = useRef(s);

  const setStep = useCallback((val) => {
    _setStep((prev) => {
      const next = typeof val === 'function' ? val(prev) : val;
      if (next !== prev) {
        directionRef.current = next > prev ? 'forward' : 'back';
      }
      stepRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!stepsEqual(prevRef.current, s)) {
      prevRef.current = s;
      const normalized = normalize(s);
      setSteps(normalized);
      setStep(normalized.length - 1);
    }
  }, [s, setStep]);

  const direction = directionRef.current;
  const currentStep = useMemo(() => steps[step]?.name, [step, steps]);
  const current = useMemo(() => steps[step], [step, steps]);

  const goBack = useCallback((withoutTrim = false) => {
    setStep((prev) => {
      if (prev === 0) return prev;
      if (withoutTrim) return prev - 1;
      setSteps((prevSteps) => prevSteps.slice(0, prev));
      return prev - 1;
    });
  }, []);

  const goNext = useCallback(() => {
    setSteps((prevSteps) => {
      setStep((prev) => (prev < prevSteps.length - 1 ? prev + 1 : prev));
      return prevSteps;
    });
  }, []);

  const setStepWithTrim = useCallback((s) => {
    setSteps((prevSteps) => {
      if (s < 0 || s >= prevSteps.length) {
        console.warn(`setStepWithTrim: step ${s} is out of bounds [0, ${prevSteps.length - 1}]`);
        return prevSteps;
      }
      setStep(s);
      return prevSteps.slice(0, s + 1);
    });
  }, []);

  const goToStep = useCallback((stepName) => {
    setSteps((prevSteps) => {
      const index = prevSteps.findIndex((s) => s.name === stepName);
      if (index === -1) {
        console.warn(`goToStep: step "${stepName}" not found`);
        return prevSteps;
      }
      setStep(index);
      return prevSteps;
    });
  }, []);

  /**
   * Navigate to a step. If a step with the same `name` already exists, navigates
   * back to it and trims everything after. Otherwise appends as a new step.
   * Safe to call without guarding for duplicates — deduplication is built in.
   */
  const nextCrumb = useCallback(
    (newStep) => {
      if (newStep.link && newStep.content) {
        showAlert({
          title:
            'BreadcrumbStep cannot have both "link" and "content". Use "link" for route navigation or "content" for inline rendering, not both.',
          color: 'danger',
          variant: 'filled'
        });
        throw new Error(
          'BreadcrumbStep cannot have both "link" and "content". Use "link" for route navigation or "content" for inline rendering, not both.'
        );
      }

      setSteps((prevSteps) => {
        const existingIndex = prevSteps.findIndex((s) => s.name === newStep.name);
        if (existingIndex !== -1) {
          directionRef.current = existingIndex > stepRef.current ? 'forward' : 'back';
          setStep(existingIndex);
          if (newStep.link) navigate(newStep.link);
          return prevSteps.slice(0, existingIndex + 1);
        }
        directionRef.current = 'forward';
        const trimmed = prevSteps.slice(0, stepRef.current + 1);
        setStep(trimmed.length);
        if (newStep.link) navigate(newStep.link);
        return [...trimmed, newStep];
      });
    },
    [setStep, navigate]
  );

  return useMemo(
    () => ({
      steps,
      step,
      setStep,
      setSteps,
      setStepWithTrim,
      currentStep,
      current,
      direction,
      nextCrumb,
      goBack,
      goNext,
      goToStep
    }),
    [
      steps,
      step,
      direction,
      currentStep,
      current,
      setStep,
      setStepWithTrim,
      nextCrumb,
      goBack,
      goNext,
      goToStep
    ]
  );
}

export default useBreadcrumb;
